import { useEffect, useState, useCallback } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { colors, formatNumber, formatPercent } from '../../styles/tokens'
import StatusBadge from '../../components/StatusBadge'
import * as XLSX from 'xlsx'
import { Button, Input, Checkbox } from '../../components/FormControls'
import { EmptyState, ErrorBanner, SuccessBanner, SkeletonRows } from '../../components/Feedback'
import ImportExportPanel from '../../components/ImportExportPanel'
import WbsManagement from '../../components/WbsManagement'
import PackagePickerModal from '../../components/PackagePickerModal'
import SetQuantityModal from '../../components/SetQuantityModal'
import QuantificationPage from './QuantificationPage'
import EstimationBreadcrumb from '../../components/EstimationBreadcrumb'

const TABS = [
  { key: 'wbs', label: 'WBS' },
  { key: 'boq', label: 'BOQ' },
  { key: 'quantification', label: 'Quantification' },
]

function ModuleNav({ tenderId, revisionId }) {
  const base = `/estimation/tender/${tenderId}/revision/${revisionId}`
  const items = [
    { to: `${base}/basis`, label: 'Basis of Estimate' },
    { to: `${base}/markup`, label: 'Markup' },
    { to: `${base}/indirect`, label: 'Indirect / Risk / HO' },
    { to: `${base}/unpriced`, label: 'Unpriced Items' },
    { to: `${base}/kpis`, label: 'Estimating KPIs' },
    { to: `${base}/summary`, label: 'Tender Summary' },
    { to: `${base}/lifecycle`, label: 'Approval / Freeze / Export' },
  ]
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
      {items.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: colors.primary,
            textDecoration: 'none',
            padding: '7px 14px',
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            background: colors.surface,
          }}
        >
          {item.label}
        </Link>
      ))}
    </div>
  )
}

export default function RevisionWorkspacePage() {
  const { tenderId, revisionId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') || 'wbs'

  const [revision, setRevision] = useState(null)
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)
  const [editingPricingDate, setEditingPricingDate] = useState(false)
  const [pricingDateDraft, setPricingDateDraft] = useState('')
  const [savingPricingDate, setSavingPricingDate] = useState(false)

  const loadRevision = useCallback(() => {
    return supabase
      .from('estimate_revision')
      .select('*')
      .eq('id', revisionId)
      .single()
      .then(({ data, error }) => {
        if (error) setError(error.message)
        else setRevision(data)
      })
  }, [revisionId])

  useEffect(() => { loadRevision() }, [loadRevision])

  async function handleSavePricingDate() {
    if (!pricingDateDraft) return
    setSavingPricingDate(true)
    setError(null)
    // pricing_date drives every resource_rate lookup across the entire
    // First-Principles chain (resolve_crew_cost_per_shift_v2 and
    // first_principles_rate_breakdown) — this write only changes that
    // single column; all downstream figures are recalculated by the
    // backend Views on next read, never in React.
    const { error } = await supabase.from('estimate_revision').update({ pricing_date: pricingDateDraft }).eq('id', revisionId)
    if (error) {
      setError(error.message)
    } else {
      setSuccessMsg('Pricing date updated — First-Principles rates will use this date going forward.')
      setEditingPricingDate(false)
      await loadRevision()
    }
    setSavingPricingDate(false)
  }

  function setTab(tab) {
    setSearchParams({ tab })
  }

  if (error && !revision) return <ErrorBanner message={error} />
  if (!revision) return <SkeletonRows rows={3} cols={3} />

  return (
    <div>
      <EstimationBreadcrumb />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '10px 0 20px', flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, margin: 0 }}>{revision.label}</h1>
        <StatusBadge status={revision.status} />

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          {!editingPricingDate ? (
            <>
              <span style={{ fontSize: 12, color: colors.textSecondary }}>
                Pricing Date: <strong style={{ color: colors.textPrimary }}>{revision.pricing_date}</strong>
              </span>
              <button
                onClick={() => { setPricingDateDraft(revision.pricing_date); setEditingPricingDate(true) }}
                style={{ background: 'none', border: 'none', color: colors.primary, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
              >
                Edit
              </button>
            </>
          ) : (
            <>
              <span style={{ fontSize: 12, color: colors.textSecondary }}>Pricing Date:</span>
              <Input type="date" value={pricingDateDraft} onChange={(e) => setPricingDateDraft(e.target.value)} style={{ fontSize: 13, padding: '5px 8px' }} />
              <Button size="sm" onClick={handleSavePricingDate} disabled={savingPricingDate}>{savingPricingDate ? 'Saving…' : 'Save'}</Button>
              <Button size="sm" variant="secondary" onClick={() => setEditingPricingDate(false)}>Cancel</Button>
            </>
          )}
        </div>
      </div>
      <p style={{ fontSize: 11, color: colors.textMuted, margin: '-14px 0 16px' }}>
        All First-Principles rates (Crew Cost, Resource Rates) are resolved against this date — not today's date.
      </p>
      <SuccessBanner message={successMsg} />
      <ErrorBanner message={error} />

      <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${colors.border}`, marginBottom: 20 }}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setTab(tab.key)}
            style={{
              padding: '10px 18px',
              cursor: 'pointer',
              border: 'none',
              borderBottom: activeTab === tab.key ? `2px solid ${colors.primary}` : '2px solid transparent',
              background: 'none',
              fontWeight: activeTab === tab.key ? 700 : 500,
              color: activeTab === tab.key ? colors.primary : colors.textSecondary,
              fontSize: 14,
              fontFamily: 'inherit',
              marginBottom: -1,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <ModuleNav tenderId={tenderId} revisionId={revisionId} />

      {activeTab === 'wbs' && <WbsManagement revisionId={revisionId} tenderId={tenderId} />}
      {activeTab === 'boq' && <BoqTab revisionId={revisionId} tenderId={tenderId} />}
      {activeTab === 'quantification' && <QuantificationPage />}
    </div>
  )
}

// ---------------------------------------------------------------------
// BOQ Tab — formatted commercial table with editable vs calculated columns
// ---------------------------------------------------------------------
// Searchable Package combobox -- reads from the shared, already-loaded
// dictionary (distinct boq_item.package_name values for this
// revision), with a "Create new" option. Selecting an existing value
// or confirming a new one calls onChange; the caller (row-level
// handler) is the only place that ever calls assign_boq_package.
// This component never writes to the database itself.
function PackageCombo({ value, options, onChange, placeholder, disabled }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ref = useState(() => Math.random().toString(36))[0]

  useEffect(() => {
    function handleClickOutside(e) {
      if (!e.target.closest(`[data-pkgcombo="${ref}"]`)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [ref])

  const normalized = (s) => (s || '').trim().toLowerCase()
  const filtered = options.filter((o) => normalized(o).includes(normalized(query)))
  const exactExists = options.some((o) => normalized(o) === normalized(query))
  const trimmedQuery = (query || '').trim()

  return (
    <div data-pkgcombo={ref} style={{ position: 'relative', width: 130 }}>
      <input
        value={open ? query : (value || '')}
        onFocus={() => { if (!disabled) { setOpen(true); setQuery('') } }}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        style={{ fontSize: 12, width: '100%', border: `1px solid ${colors.border}`, borderRadius: 4, padding: '4px 6px', boxSizing: 'border-box' }}
      />
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 20, marginTop: 2, minWidth: 160,
          background: '#FFF', border: `1px solid ${colors.border}`, borderRadius: 6,
          maxHeight: 180, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        }}>
          {value && (
            <div onClick={() => { onChange(null); setOpen(false) }}
              style={{ padding: '6px 10px', fontSize: 12, color: colors.textMuted, cursor: 'pointer', borderBottom: `1px solid ${colors.border}` }}>
              ✕ Clear (Unassigned)
            </div>
          )}
          {filtered.map((o) => (
            <div key={o} onClick={() => { onChange(o); setOpen(false) }}
              style={{ padding: '6px 10px', fontSize: 12, cursor: 'pointer' }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#F1F5F9'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
              {o}
            </div>
          ))}
          {trimmedQuery && !exactExists && (
            <div onClick={() => { onChange(trimmedQuery); setOpen(false) }}
              style={{ padding: '6px 10px', fontSize: 12, cursor: 'pointer', color: colors.textSecondary, fontStyle: 'italic', borderTop: filtered.length ? `1px solid ${colors.border}` : 'none' }}>
              + Create "{trimmedQuery}"
            </div>
          )}
          {filtered.length === 0 && !trimmedQuery && (
            <div style={{ padding: '6px 10px', fontSize: 11, color: colors.textMuted }}>Type to search or create…</div>
          )}
        </div>
      )}
    </div>
  )
}

// Same strict numeric normalization used by TenderSummaryPage (P1) /
// RevisionComparisonPage (P2): a present-but-non-numeric financial value
// throws rather than silently becoming 0.
function toFinancialNumber(value, fieldPath) {
  if (value === undefined || value === null) return 0
  const n = Number(value)
  if (!Number.isFinite(n)) {
    throw new Error(`Frozen snapshot contains a non-numeric financial value at ${fieldPath}: ${JSON.stringify(value)}`)
  }
  return n
}

// P3 (BOQ Fully-Loaded Pricing): mirrors boq_item_fully_loaded_pricing's
// exact live formula -- allocation_pct is direct_cost / SUM(direct_cost)
// over items with direct_cost > 0 only (proven NOT equal to the
// frozen_snapshot.direct_cost_total column, which sums ALL items
// including non-positive ones), then each overhead/contingency total is
// distributed by that same percentage. No new financial rule: this is
// the live View's formula, evaluated over the immutable full_boq_data
// instead of live tables.
function buildFrozenFullyLoadedMap(boqData, generalSiteOverheadTotal, hoOverheadTotal, generalContingencyTotal) {
  const eligibleTotal = boqData.reduce((s, it) => {
    const dc = toFinancialNumber(it.direct_cost, 'full_boq_data[].direct_cost')
    return dc > 0 ? s + dc : s
  }, 0)

  const map = {}
  boqData.forEach((it) => {
    const dc = toFinancialNumber(it.direct_cost, 'full_boq_data[].direct_cost')
    const allocPct = (dc > 0 && eligibleTotal > 0) ? dc / eligibleTotal : 0
    const allocatedGso = allocPct * generalSiteOverheadTotal
    const allocatedHo = allocPct * hoOverheadTotal
    const allocatedGc = allocPct * generalContingencyTotal
    const specificSiteOverhead = toFinancialNumber(it.boq_specific_site_overhead, 'full_boq_data[].boq_specific_site_overhead')
    const specificRisk = toFinancialNumber(it.boq_specific_risk, 'full_boq_data[].boq_specific_risk')
    const fullyLoadedCost = dc + specificSiteOverhead + specificRisk + allocatedGso + allocatedHo + allocatedGc
    const markupPct = toFinancialNumber(it.effective_markup_pct, 'full_boq_data[].effective_markup_pct')
    map[it.id] = {
      allocated_general_site_overhead: allocatedGso,
      allocated_ho_overhead: allocatedHo,
      allocated_general_contingency: allocatedGc,
      fully_loaded_cost: fullyLoadedCost,
      fully_loaded_selling_price: fullyLoadedCost * (1 + markupPct / 100),
    }
  })
  return map
}

function BoqTab({ revisionId, tenderId }) {
  const [items, setItems] = useState(null)
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)
  const [form, setForm] = useState({ item_no: '', description: '', unit: '', resolved_quantity: '' })
  const [submitting, setSubmitting] = useState(false)
  const [confirmingExcludeId, setConfirmingExcludeId] = useState(null)
  const [togglingId, setTogglingId] = useState(null)
  const [revisionStatus, setRevisionStatus] = useState(null)
  const [quantityModalItem, setQuantityModalItem] = useState(null)
  const [costCodes, setCostCodes] = useState([])
  const [savingRow, setSavingRow] = useState(null)
  // Filters -- classification only, never touch pricing.
  const [packageFilter, setPackageFilter] = useState('all')
  const [costCodeFilter, setCostCodeFilter] = useState('all')
  const [searchText, setSearchText] = useState('')

  const load = useCallback(async () => {
    const { data: revData } = await supabase.from('estimate_revision').select('status').eq('id', revisionId).single()
    const status = revData?.status ?? null
    setRevisionStatus(status)

    // Tender-scoped: explicit filter, not left to RLS alone for page-
    // level scoping. A user authorized for multiple Tenders must never
    // see combined Cost Codes from more than one Tender here.
    const { data: cc } = await supabase.from('canonical_cost_code').select('id, code, description').eq('tender_id', tenderId)
    setCostCodes(cc || [])

    if (status === 'frozen') {
      // P3: same source-of-truth split already used by TenderSummaryPage
      // (P1) / RevisionComparisonPage (P2) -- Frozen never reads the live
      // boq_item / boq_item_selling_price / boq_item_fully_loaded_pricing
      // tables/Views; everything comes from the immutable freeze_snapshot.
      const snapRes = await supabase
        .from('freeze_snapshot')
        .select('ho_overhead_total, full_boq_data, full_indirect_data')
        .eq('estimate_revision_id', revisionId)
        .maybeSingle()
      if (snapRes.error) return setError(snapRes.error.message)
      if (!snapRes.data) return setError('This revision is marked frozen but no snapshot record could be found.')

      try {
        const boqData = Array.isArray(snapRes.data.full_boq_data) ? snapRes.data.full_boq_data : []
        const indirect = snapRes.data.full_indirect_data && typeof snapRes.data.full_indirect_data === 'object' ? snapRes.data.full_indirect_data : {}
        const fullyLoadedMap = buildFrozenFullyLoadedMap(
          boqData,
          toFinancialNumber(indirect.general_site_overhead_total, 'full_indirect_data.general_site_overhead_total'),
          toFinancialNumber(snapRes.data.ho_overhead_total, 'ho_overhead_total'),
          toFinancialNumber(indirect.general_contingency_total, 'full_indirect_data.general_contingency_total'),
        )
        setItems(boqData.map((item) => ({
          id: item.id, item_no: item.item_no, description: item.description, unit: item.unit,
          resolved_quantity: item.resolved_quantity, is_excluded: item.is_excluded,
          package_name: item.package_name, package_id: item.package_id, canonical_cost_code_id: item.canonical_cost_code_id,
          pricing: { direct_cost: item.direct_cost, cost_base: item.cost_base, effective_markup_pct: item.effective_markup_pct, selling_price: item.selling_price },
          fullyLoaded: fullyLoadedMap[item.id],
          sourceType: null, // historical provenance detail not part of freeze_snapshot; intentionally blank, not fabricated
        })))
      } catch (e) {
        setError(e.message)
      }
      return
    }

    const { data: boqItems, error: boqError } = await supabase
      .from('boq_item')
      .select('id, item_no, description, unit, resolved_quantity, is_excluded, package_name, package_id, canonical_cost_code_id')
      .eq('estimate_revision_id', revisionId)
      .order('item_no')

    if (boqError) {
      setError(boqError.message)
      return
    }

    const { data: pricing, error: pricingError } = await supabase
      .from('boq_item_selling_price')
      .select('boq_item_id, direct_cost, cost_base, effective_markup_pct, selling_price')
      .eq('estimate_revision_id', revisionId)

    if (pricingError) {
      setError(pricingError.message)
      return
    }

    // Phase 1 — Derived Fully Loaded Pricing. Read-only presentation
    // layer from boq_item_fully_loaded_pricing (approved business rule).
    // Kept in a SEPARATE map (fullyLoaded, not merged into `pricing`)
    // so the existing official selling_price/cost_base/direct_cost
    // fields above remain untouched and clearly distinguishable from
    // this new derived representation. No allocation math happens
    // here -- every value is read verbatim from the view.
    const { data: fullyLoaded, error: flError } = await supabase
      .from('boq_item_fully_loaded_pricing')
      .select('boq_item_id, allocated_general_site_overhead, allocated_ho_overhead, allocated_general_contingency, fully_loaded_cost, fully_loaded_selling_price')
      .eq('estimate_revision_id', revisionId)

    if (flError) {
      setError(flError.message)
      return
    }

    // Active quantity source per item, for the Provenance badge. Read-only
    // — this component never writes here; all writes go through
    // recapture_quantity_source() via SetQuantityModal.
    const { data: sources, error: sourcesError } = await supabase
      .from('quantity_source')
      .select('boq_item_id, source_type')
      .eq('is_active', true)
      .in('boq_item_id', boqItems.map((b) => b.id))

    if (sourcesError) {
      setError(sourcesError.message)
      return
    }

    const pricingMap = Object.fromEntries((pricing || []).map((p) => [p.boq_item_id, p]))
    const fullyLoadedMap = Object.fromEntries((fullyLoaded || []).map((f) => [f.boq_item_id, f]))
    const sourceMap = Object.fromEntries((sources || []).map((s) => [s.boq_item_id, s.source_type]))
    setItems(boqItems.map((item) => ({ ...item, pricing: pricingMap[item.id], fullyLoaded: fullyLoadedMap[item.id], sourceType: sourceMap[item.id] || null })))
  }, [revisionId])

  useEffect(() => {
    load()
  }, [load])

  async function handleAdd(e) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    setSuccessMsg(null)

    const { error } = await supabase.from('boq_item').insert({
      estimate_revision_id: revisionId,
      item_no: form.item_no,
      description: form.description,
      unit: form.unit,
      resolved_quantity: form.resolved_quantity ? Number(form.resolved_quantity) : 0,
    })

    if (error) {
      setError(error.message)
    } else {
      setSuccessMsg(`Item "${form.item_no}" added.`)
      setForm({ item_no: '', description: '', unit: '', resolved_quantity: '' })
      await load()
    }
    setSubmitting(false)
  }

  // Toggling is_excluded is the ONLY write here. The entire calculation
  // chain (boq_item_cost_base, boq_item_selling_price, ...
  // estimate_revision_calculation_chain) already filters on this exact
  // column server-side -- this component performs no pricing logic of
  // its own, it only flips the flag the backend already respects.
  async function handleToggleExclude(item) {
    setTogglingId(item.id)
    setError(null)
    const { error } = await supabase.from('boq_item').update({ is_excluded: !item.is_excluded }).eq('id', item.id)
    if (error) {
      setError(error.message)
    } else {
      setSuccessMsg(item.is_excluded ? `Item "${item.item_no}" included back in pricing.` : `Item "${item.item_no}" excluded from pricing.`)
      setConfirmingExcludeId(null)
      await load()
    }
    setTogglingId(null)
  }

  const totalDirect = (items || []).reduce((sum, i) => sum + Number(i.pricing?.direct_cost || 0), 0)
  const totalSelling = (items || []).reduce((sum, i) => sum + Number(i.pricing?.selling_price || 0), 0)
  // Phase 1 totals -- summed straight from the derived view's per-item
  // values (never recomputed independently), so they match the view's
  // own reconciliation exactly.
  const totalAllocatedIndirect = (items || []).reduce((sum, i) => sum
    + (Number(i.fullyLoaded?.allocated_general_site_overhead) || 0)
    + (Number(i.fullyLoaded?.allocated_ho_overhead) || 0)
    + (Number(i.fullyLoaded?.allocated_general_contingency) || 0), 0)
  const totalFullyLoadedCost = (items || []).reduce((sum, i) => sum + (Number(i.fullyLoaded?.fully_loaded_cost) || 0), 0)
  const totalFullyLoadedSelling = (items || []).reduce((sum, i) => sum + (Number(i.fullyLoaded?.fully_loaded_selling_price) || 0), 0)

  // BOQ Excel Export -- CostSpace BOQ.jsx importer contract, approved
  // mapping. Exports the FINAL price (Fully Loaded Selling Price --
  // Direct + Specific Indirect + Allocated General Indirect + Markup),
  // not the intermediate Direct + Markup Price, per the corrected
  // business decision. Final Unit Rate is derived at export time only
  // (never read from or written to boq_item.selling_unit_rate, which
  // is not the authoritative pricing source -- confirmed in the SSOT
  // audit). package_name is read directly from the real BOQ Package
  // SSOT (boq_item.package_name, assigned via assign_boq_package) --
  // no derivation, no invention.
  async function handleExportBoq() {
    // Tender-scoped -- same rationale as the load() call above.
    const { data: costCodes } = await supabase.from('canonical_cost_code').select('id, code').eq('tender_id', tenderId)
    const ccMap = Object.fromEntries((costCodes || []).map((c) => [c.id, c.code]))

    const headers = ['item_no', 'description', 'unit', 'qty_tendered', 'selling_unit_rate', 'total_selling', 'cost_code', 'package_name']
    const rows = (items || [])
      .filter((it) => !it.is_excluded)
      .map((it) => {
        const qty = Number(it.resolved_quantity) || 0
        const finalSelling = Number(it.fullyLoaded?.fully_loaded_selling_price) || 0
        const unitRate = qty > 0 ? finalSelling / qty : null
        return [
          it.item_no || '', it.description || '', it.unit || '', qty,
          unitRate != null ? Math.round(unitRate * 100) / 100 : '',
          Math.round(finalSelling * 100) / 100,
          ccMap[it.canonical_cost_code_id] || '',
          it.package_name || '',
        ]
      })

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])
    ws['!cols'] = headers.map(() => ({ wch: 16 }))
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'BOQ')
    XLSX.writeFile(wb, `boq_${tenderId}_${revisionId}.xlsx`)
  }

  // Classification-only mutations -- both go through the approved
  // RPCs (assign_boq_package / assign_boq_cost_code), never a direct
  // UPDATE. Both RPCs enforce their own permission check and, on a
  // frozen revision, the frozen-mutation trigger performs its own
  // independent re-check -- neither path can be bypassed from here.
  const [packagePickerItemId, setPackagePickerItemId] = useState(null)

  // Assigns via package_id -- the trigger derives package_name from the
  // Package Master. package_name is never sent independently here.
  async function handleAssignPackageId(itemId, packageId) {
    setSavingRow(itemId + 'package')
    setError(null)
    const { error } = await supabase.rpc('assign_boq_package', { p_boq_item_id: itemId, p_package_id: packageId })
    if (error) setError(error.message)
    else { setSuccessMsg('Package saved.'); await load() }
    setSavingRow(null)
    setPackagePickerItemId(null)
  }

  async function handleAssignCostCode(itemId, costCodeId) {
    setSavingRow(itemId + 'costcode')
    setError(null)
    const { error } = await supabase.rpc('assign_boq_cost_code', { p_boq_item_id: itemId, p_cost_code_id: costCodeId || null })
    if (error) setError(error.message)
    else { setSuccessMsg('Cost Code saved.'); await load() }
    setSavingRow(null)
  }

  // Known packages/cost codes for this revision, for the filter
  // dropdowns -- derived from already-loaded items, no new query.
  const knownPackages = [...new Set((items || []).map((i) => i.package_name).filter(Boolean))].sort()

  const filteredItems = (items || []).filter((it) => {
    if (packageFilter === 'unassigned' && it.package_name) return false
    if (packageFilter !== 'all' && packageFilter !== 'unassigned' && it.package_name !== packageFilter) return false
    if (costCodeFilter === 'unassigned' && it.canonical_cost_code_id) return false
    if (costCodeFilter !== 'all' && costCodeFilter !== 'unassigned' && it.canonical_cost_code_id !== costCodeFilter) return false
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase()
      if (!(it.description || '').toLowerCase().includes(q) && !(it.item_no || '').toLowerCase().includes(q)) return false
    }
    return true
  })

  return (
    <div>
      <ErrorBanner message={error} />
      <SuccessBanner message={successMsg} />

      <ImportExportPanel
        tenderId={tenderId}
        revisionId={revisionId}
        isFrozen={revisionStatus === 'frozen'}
        onImported={load}
      />

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 12 }}>
        <Link
          to={`/estimation/tender/${tenderId}/revision/${revisionId}/takeoff`}
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: colors.primary,
            textDecoration: 'none',
            padding: '7px 14px',
            border: `1px solid ${colors.primary}`,
            borderRadius: 6,
          }}
        >
          Takeoff →
        </Link>
        <Link
          to={`/estimation/tender/${tenderId}/revision/${revisionId}/rate-build-up`}
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: colors.primary,
            textDecoration: 'none',
            padding: '7px 14px',
            border: `1px solid ${colors.primary}`,
            borderRadius: 6,
          }}
        >
          Manage Rate Build-Up →
        </Link>
        <button onClick={handleExportBoq} type="button"
          style={{ padding: '8px 14px', background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          📤 Export to CostSpace
        </button>
      </div>

      <form
        onSubmit={handleAdd}
        style={{
          display: 'flex',
          gap: 10,
          marginBottom: 20,
          flexWrap: 'wrap',
          padding: 16,
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: 8,
        }}
      >
        <Input
          value={form.item_no}
          onChange={(e) => setForm({ ...form, item_no: e.target.value })}
          placeholder="Item No"
          required
          style={{ width: 100 }}
        />
        <Input
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Description"
          required
          style={{ flex: 1, minWidth: 220 }}
        />
        <Input
          value={form.unit}
          onChange={(e) => setForm({ ...form, unit: e.target.value })}
          placeholder="Unit"
          required
          style={{ width: 90 }}
        />
        <Input
          value={form.resolved_quantity}
          onChange={(e) => setForm({ ...form, resolved_quantity: e.target.value })}
          placeholder="Quantity"
          type="number"
          step="any"
          style={{ width: 110 }}
        />
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Adding…' : '+ Add Item'}
        </Button>
      </form>

      {items && items.length > 0 && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
          <input value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="Search description / item no…"
            style={{ padding: '7px 10px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 13, width: 220 }} />
          <select value={packageFilter} onChange={(e) => setPackageFilter(e.target.value)}
            style={{ padding: '7px 10px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 13 }}>
            <option value="all">All Packages</option>
            <option value="unassigned">⚠ Unassigned Package</option>
            {knownPackages.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={costCodeFilter} onChange={(e) => setCostCodeFilter(e.target.value)}
            style={{ padding: '7px 10px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 13 }}>
            <option value="all">All Cost Codes</option>
            <option value="unassigned">⚠ Unassigned Cost Code</option>
            {costCodes.map((cc) => <option key={cc.id} value={cc.id}>{cc.code} — {cc.description}</option>)}
          </select>
          <span style={{ fontSize: 12, color: colors.textMuted }}>{filteredItems.length} of {items.length} items</span>
        </div>
      )}

      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {items === null && <div style={{ padding: 20 }}><SkeletonRows rows={3} cols={7} /></div>}

        {items?.length === 0 && (
          <div style={{ padding: 24 }}>
            <EmptyState title="No BOQ items yet" description="Add the first line item to begin pricing this revision." />
          </div>
        )}

        {filteredItems.length === 0 && items?.length > 0 && (
          <div style={{ padding: 24 }}>
            <EmptyState title="No items match the current filters" description="Try clearing the search, Package, or Cost Code filter." />
          </div>
        )}

        {filteredItems.length > 0 && (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
                  <Th>No.</Th>
                  <Th>Description</Th>
                  <Th>Package</Th>
                  <Th>Cost Code</Th>
                  <Th>Unit</Th>
                  <Th align="right">Qty</Th>
                  <ThCalc align="right">Direct Cost</ThCalc>
                  <ThCalc align="right">Markup %</ThCalc>
                  <ThCalc align="right">Direct + Markup Price</ThCalc>
                  {/* Phase 1 — Derived Fully Loaded Pricing (separate from the
                      official Direct + Markup Price above). Read-only presentation of
                      General Site Overhead + HO + General Contingency
                      allocated by direct-cost share, per the approved
                      business rule. */}
                  <ThCalc align="right" style={{ background: '#FFF7ED' }}>Allocated Indirect</ThCalc>
                  <ThCalc align="right" style={{ background: '#FFF7ED' }}>Fully Loaded Cost</ThCalc>
                  <ThCalc align="right" style={{ background: '#FFF7ED' }}>Fully Loaded Selling Price</ThCalc>
                  {/* Final Unit Rate for CostSpace export -- derived
                      (never stored/backfilled) from the FINAL price
                      (Fully Loaded Selling Price), not the intermediate
                      Direct + Markup Price. NULL for qty=0 items,
                      never invented. */}
                  <ThCalc align="right" style={{ background: '#FFF7ED' }}>Final Unit Rate</ThCalc>
                  <Th>Quantity Source</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item.id} style={{ borderBottom: `1px solid ${colors.border}`, opacity: item.is_excluded ? 0.55 : 1 }}>
                    <Td muted>{item.item_no}</Td>
                    <Td>{item.description}</Td>
                    <Td>
                      <button onClick={() => setPackagePickerItemId(item.id)} disabled={savingRow === item.id + 'package'}
                        style={{ fontSize: 12, padding: '4px 8px', border: `1px solid ${colors.border}`, borderRadius: 4, background: '#fff', cursor: 'pointer', width: '100%', textAlign: 'left' }}>
                        {item.package_name || '— Select —'}
                      </button>
                    </Td>
                    <Td>
                      <select value={item.canonical_cost_code_id || ''} disabled={savingRow === item.id + 'costcode'}
                        onChange={(e) => handleAssignCostCode(item.id, e.target.value || null)}
                        style={{ fontSize: 12, minWidth: 130, border: `1px solid ${colors.border}`, borderRadius: 4, padding: '4px 6px' }}>
                        <option value="">⚠ Not assigned</option>
                        {costCodes.map((cc) => <option key={cc.id} value={cc.id}>{cc.code}</option>)}
                      </select>
                    </Td>
                    <Td muted>{item.unit}</Td>
                    <Td align="right">{formatNumber(item.resolved_quantity)}</Td>
                    <TdCalc align="right">{item.is_excluded ? '—' : formatNumber(item.pricing?.direct_cost)}</TdCalc>
                    <TdCalc align="right">{item.is_excluded ? '—' : formatPercent(item.pricing?.effective_markup_pct)}</TdCalc>
                    <TdCalc align="right" strong>{item.is_excluded ? '—' : formatNumber(item.pricing?.selling_price)}</TdCalc>
                    <TdCalc align="right" style={{ background: '#FFFBF5' }}>
                      {item.is_excluded ? '—' : formatNumber(
                        (Number(item.fullyLoaded?.allocated_general_site_overhead) || 0)
                        + (Number(item.fullyLoaded?.allocated_ho_overhead) || 0)
                        + (Number(item.fullyLoaded?.allocated_general_contingency) || 0)
                      )}
                    </TdCalc>
                    <TdCalc align="right" style={{ background: '#FFFBF5' }}>{item.is_excluded ? '—' : formatNumber(item.fullyLoaded?.fully_loaded_cost)}</TdCalc>
                    <TdCalc align="right" strong style={{ background: '#FFFBF5' }}>{item.is_excluded ? '—' : formatNumber(item.fullyLoaded?.fully_loaded_selling_price)}</TdCalc>
                    <TdCalc align="right" style={{ background: '#FFFBF5' }}>
                      {item.is_excluded || !Number(item.resolved_quantity) ? '—' :
                        formatNumber(Number(item.fullyLoaded?.fully_loaded_selling_price) / Number(item.resolved_quantity))}
                    </TdCalc>
                    <Td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {item.sourceType ? (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 10,
                            background: item.sourceType === 'manual_entry' ? '#EEF2FF' : '#F0FDF4',
                            border: `1px solid ${item.sourceType === 'manual_entry' ? colors.primary : colors.accentGreen}`,
                            fontSize: 10, fontWeight: 700, color: item.sourceType === 'manual_entry' ? colors.primary : colors.accentGreen,
                          }}>
                            {item.sourceType === 'manual_entry' ? '✏ Manual' : item.sourceType === 'takeoff_derived' ? '📐 Canvas' : item.sourceType}
                          </span>
                        ) : (
                          <span style={{ fontSize: 10, color: colors.textMuted }}>— Not set</span>
                        )}
                        <button
                          onClick={() => setQuantityModalItem(item)}
                          style={{ background: 'none', border: 'none', color: colors.primary, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                        >
                          Set Quantity
                        </button>
                      </div>
                    </Td>
                    <Td>
                      {item.is_excluded ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', padding: '3px 9px', borderRadius: 12,
                            background: '#F3F4F6', border: '1px solid #D1D5DB', fontSize: 11, fontWeight: 700, color: '#6B7280',
                          }}>
                            Excluded
                          </span>
                          <button
                            onClick={() => handleToggleExclude(item)}
                            disabled={togglingId === item.id}
                            style={{ background: 'none', border: 'none', color: colors.primary, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                          >
                            {togglingId === item.id ? 'Including…' : 'Include'}
                          </button>
                        </div>
                      ) : confirmingExcludeId === item.id ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 11, color: colors.textSecondary }}>Exclude this item?</span>
                          <button
                            onClick={() => handleToggleExclude(item)}
                            disabled={togglingId === item.id}
                            style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}
                          >
                            {togglingId === item.id ? '…' : 'Confirm'}
                          </button>
                          <button
                            onClick={() => setConfirmingExcludeId(null)}
                            style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', fontSize: 12 }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmingExcludeId(item.id)}
                          style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', fontSize: 12 }}
                        >
                          Exclude
                        </button>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: `2px solid ${colors.borderStrong}` }}>
                  <td></td>
                  <td style={{ padding: '12px 20px', textAlign: 'left', fontWeight: 700, fontSize: 13, color: colors.textSecondary }}>
                    TOTAL
                  </td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td style={{ padding: '12px 20px', textAlign: 'right', fontWeight: 700, fontSize: 13, color: colors.textSecondary }}>
                    {formatNumber(totalDirect)}
                  </td>
                  <td></td>
                  <td style={{ padding: '12px 20px', textAlign: 'right', fontWeight: 700, fontSize: 15, color: colors.primary, background: '#F8FAFF' }}>
                    {formatNumber(totalSelling)}
                  </td>
                  <td style={{ padding: '12px 20px', textAlign: 'right', fontWeight: 700, fontSize: 13, color: colors.textSecondary, background: '#FFFBF5' }}>
                    {formatNumber(totalAllocatedIndirect)}
                  </td>
                  <td style={{ padding: '12px 20px', textAlign: 'right', fontWeight: 700, fontSize: 13, color: colors.textSecondary, background: '#FFFBF5' }}>
                    {formatNumber(totalFullyLoadedCost)}
                  </td>
                  <td style={{ padding: '12px 20px', textAlign: 'right', fontWeight: 700, fontSize: 15, color: '#B45309', background: '#FFFBF5' }}>
                    {formatNumber(totalFullyLoadedSelling)}
                  </td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
            <p style={{ fontSize: 12, color: colors.textMuted, padding: '10px 20px', margin: 0, borderTop: `1px solid ${colors.border}` }}>
              Shaded columns are calculated live from <code>boq_item_selling_price</code> and will read 0 until
              Rate Build-Up lines exist for an item (next phase).
              <br />
              <strong>Amber columns — Phase 1, derived Fully Loaded Pricing:</strong> Allocated Indirect is this
              item's share of General Site Overhead + HO Overhead + General Contingency (allocated by direct-cost
              proportion, per the approved business rule). This is a separate presentation layer and does not
              change the official Selling Price or Tender Price above.
            </p>
          </>
        )}
      </div>

      {quantityModalItem && (
        <SetQuantityModal
          boqItem={quantityModalItem}
          tenderId={tenderId}
          revisionId={revisionId}
          isFrozen={revisionStatus === 'frozen'}
          onClose={() => setQuantityModalItem(null)}
          onApplied={async () => {
            setQuantityModalItem(null)
            setSuccessMsg('Quantity applied.')
            await load()
          }}
        />
      )}
      {packagePickerItemId && (
        <PackagePickerModal
          tenderId={tenderId}
          currentPackageId={items?.find((i) => i.id === packagePickerItemId)?.package_id}
          onSelect={(packageId) => handleAssignPackageId(packagePickerItemId, packageId)}
          onClose={() => setPackagePickerItemId(null)}
        />
      )}
    </div>
  )
}

function Th({ children, align = 'left' }) {
  return (
    <th style={{ textAlign: align, padding: '10px 20px', fontSize: 12, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>
      {children}
    </th>
  )
}

function ThCalc({ children, align = 'left' }) {
  return (
    <th style={{ textAlign: align, padding: '10px 20px', fontSize: 12, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4, background: '#F8FAFF' }}>
      {children}
    </th>
  )
}

function Td({ children, align = 'left', muted }) {
  return (
    <td style={{ textAlign: align, padding: '12px 20px', color: muted ? colors.textMuted : colors.textPrimary, fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>
      {children}
    </td>
  )
}

function TdCalc({ children, align = 'left', strong }) {
  return (
    <td
      style={{
        textAlign: align,
        padding: '12px 20px',
        color: strong ? colors.primary : colors.textSecondary,
        fontSize: 14,
        fontWeight: strong ? 700 : 400,
        fontVariantNumeric: 'tabular-nums',
        background: '#FBFCFF',
      }}
    >
      {children}
    </td>
  )
}
